import {
  REPORT_DATE_MODE,
  getCurrentReportDateFilters,
  type ReportDateModeValue,
  type ReportDateRange,
} from '@/lib/report-date-range'
import { z } from 'zod/v4'
import type { DepositStatusFilter, DepositsReportAppliedFilters } from './deposits.types'

export const DEPOSIT_STATUS = {
  ALL: 'all',
  PAID: 'paid',
  PENDING: 'pending',
  IN_TRANSIT: 'in_transit',
  FAILED: 'failed',
  CANCELED: 'canceled',
} as const satisfies Record<string, DepositStatusFilter>

const dateStringSchema = z.iso.date('Invalid date')
const dateModeSchema = z.enum([
  REPORT_DATE_MODE.DAY,
  REPORT_DATE_MODE.WEEK,
  REPORT_DATE_MODE.CUSTOM,
])
const statusSchema = z.enum(Object.values(DEPOSIT_STATUS))

export const depositsReportSearchSchema = z
  .object({
    start_date: dateStringSchema.optional(),
    end_date: dateStringSchema.optional(),
    date_mode: dateModeSchema.optional(),
    status: statusSchema.optional(),
    sales_order_id: z.coerce.number().int().positive().optional(),
    page: z.coerce.number().int().positive().optional(),
    page_size: z.coerce.number().int().positive().max(100).optional(),
  })
  .refine(
    ({ start_date, end_date }) => (!start_date && !end_date) || Boolean(start_date && end_date),
    { message: 'Choose a valid start and end date', path: ['end_date'] }
  )
  .refine(({ start_date, end_date }) => !start_date || !end_date || start_date <= end_date, {
    message: 'Start date must be on or before end date',
    path: ['end_date'],
  })

export type DepositsReportSearch = z.infer<typeof depositsReportSearchSchema>

export const depositDetailSearchSchema = z.object({
  entry_id: z.coerce.number().int().positive().optional(),
  sales_order_id: z.coerce.number().int().positive().optional(),
})

/** The deposit detail's search: an entry or order to highlight, sent to the API as context. */
export type DepositDetailSearch = z.infer<typeof depositDetailSearchSchema>

interface DepositsReportPageFilters {
  status: DepositStatusFilter
  sales_order_id: number | undefined
  page: number
  page_size: number
}

/** A date range: the one searched, else the default (month to date). */
interface DepositsReportDateRangeFilters extends DepositsReportPageFilters, ReportDateRange {
  date_mode: ReportDateModeValue
}

/** An order deep link without dates; the API spans every deposit connected to the order. */
interface DepositsReportOrderFilters extends DepositsReportPageFilters {
  sales_order_id: number
  start_date?: undefined
  end_date?: undefined
  date_mode?: undefined
}

/** The filters the deposits report requests. */
export type DepositsReportFilters = DepositsReportDateRangeFilters | DepositsReportOrderFilters

type DepositsReportDefaultFilters = ReportDateRange & { date_mode: ReportDateModeValue }

export function getDepositsReportFiltersFromSearch(
  search: unknown,
  defaultFilters: DepositsReportDefaultFilters = getCurrentReportDateFilters()
): DepositsReportFilters {
  const parsed = depositsReportSearchSchema.parse(search)
  const shared = {
    status: parsed.status ?? DEPOSIT_STATUS.ALL,
    sales_order_id: parsed.sales_order_id,
    page: parsed.page ?? 1,
    page_size: parsed.page_size ?? 20,
  }

  if (parsed.start_date && parsed.end_date) {
    return {
      ...shared,
      start_date: parsed.start_date,
      end_date: parsed.end_date,
      date_mode: parsed.date_mode ?? REPORT_DATE_MODE.CUSTOM,
    }
  }

  if (parsed.sales_order_id) {
    return { ...shared, sales_order_id: parsed.sales_order_id }
  }

  return {
    ...shared,
    start_date: defaultFilters.start_date,
    end_date: defaultFilters.end_date,
    date_mode: defaultFilters.date_mode,
  }
}

/** The API's applied filters leave out the date mode, so the requested one stays. */
type DepositsReportShownAppliedFilters = DepositsReportAppliedFilters & {
  date_mode?: ReportDateModeValue | undefined
}

/** The requested filters, or once the report for them has loaded, the filters the API applied. */
export type DepositsReportDisplayedFilters =
  | DepositsReportFilters
  | DepositsReportShownAppliedFilters

export function getDisplayedDepositsReportFilters(
  requestedFilters: DepositsReportFilters,
  responseFilters: DepositsReportAppliedFilters | undefined,
  isPlaceholderData: boolean
): DepositsReportDisplayedFilters {
  if (isPlaceholderData || !responseFilters) {
    return { ...requestedFilters }
  }

  return { ...requestedFilters, ...responseFilters }
}
